import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage, API_BASE } from "../lib/api";
import { ROLE_LABELS } from "../lib/roles";
import { fmtDateTime } from "../lib/tz";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import PhotoAttachmentsField from "../components/PhotoAttachmentsField";
import AttachmentViewer from "../components/AttachmentViewer";
import ResendReportEmailButton from "../components/ResendReportEmailButton";
import BulkResendBar from "../components/BulkResendBar";
import useBulkSelection from "../hooks/useBulkSelection";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import { useAuth } from "../contexts/AuthContext";
import StickyFiltersBar, { useFullscreenRoute } from "../components/StickyFiltersBar";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";
import { Plus, AlertOctagon, Trash2, Sparkles, Loader2, Image as ImageIcon } from "lucide-react";
import { usePermission } from "../contexts/AuthContext";
import { AdminCommentsPanel } from "../components/AdminCommentsPanel";
import ApprovalActions, { ApprovalPill } from "../components/ApprovalActions";
import { toast } from "sonner";

const INCIDENT_TYPES = [
  ["", "—"],
  ["theft", "Theft"],
  ["trespass", "Trespass"],
  ["medical", "Medical"],
  ["safety", "Safety"],
  ["suspicious_activity", "Suspicious activity"],
  ["fire", "Fire"],
  ["property_damage", "Property damage"],
  ["vandalism", "Vandalism"],
  ["noise", "Noise"],
  ["access_violation", "Access violation"],
  ["parking_violation", "Parking violation"],
  ["other", "Other"],
];

const empty = {
  site_id: "",
  guard_id: "",
  title: "",
  description: "",
  severity: "low",
  status: null,   // iter238 — null by default; supervisor-only field
  incident_type: "",
  occurred_at: "",
  location_on_site: "",
  victim_name: "",
  witness_names: [],
  suspect_description: "",
  police_called: false,
  police_report_no: "",
  injuries: false,
  injuries_description: "",
  property_damage: false,
  property_damage_description: "",
  property_damage_value: "",
  action_taken: "",
  follow_up_needed: false,
  follow_up_notes: "",
  photo_attachments: [],
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Incidents() {
  const canCreateIncident = usePermission("incidents:create");
  const canDeleteIncident = usePermission("incidents:delete");
  const { user } = useAuth();
  // iter238 — only supervisor-and-above may set/change the report status.
  // Kept as a lowercase compare to be resilient to legacy role casing.
  const canEditStatus = ["supervisor", "admin", "super_admin", "chief_security_officer",
                          "operations_manager", "dispatcher", "cso"].includes(
                          (user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);  const [guards, setGuards] = useState([]);
  const [onShiftOfficers, setOnShiftOfficers] = useState([]); // /live-map/officers rows
  const [guardsOnShiftOnly, setGuardsOnShiftOnly] = useState(true); // toggle in form
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [summarizing, setSummarizing] = useState(null);
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [filters, setFilters] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const bulk = useBulkSelection();
  const location = useLocation();
  const isFs = useFullscreenRoute(location.pathname);
  useRememberWindowGeometry(isFs ? location.pathname : null);

  const load = useCallback(async () => {
    const [a, b, c, o] = await Promise.all([
      api.get("/incidents"),
      api.get("/sites"),
      api.get("/guards"),
      api.get("/live-map/officers").catch(() => ({ data: [] })),
    ]);
    setItems(a.data); setSites(b.data); setGuards(c.data);
    setOnShiftOfficers(o.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // On "New Incident" open, default the site to the currently signed-in guard's
  // active shift site (mobile /active-shift). Supervisors/admins get no default
  // (they pick from the searchable dropdown).
  useEffect(() => {
    if (!open || editing) return;
    if (form.site_id) return; // don't clobber a user-picked value
    let cancelled = false;
    api.get("/mobile/active-shift").then((r) => {
      if (cancelled) return;
      const siteId = r.data?.active?.site?.id;
      if (siteId) {
        setForm((f) => (f.site_id ? f : { ...f, site_id: siteId, guard_id: f.guard_id || r.data.active.shift?.guard_id || "" }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, editing, form.site_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    if (!form.site_id || !form.title) { toast.error("Site & title required"); return; }
    try {
      const payload = {
        ...form,
        guard_id: form.guard_id || null,
        incident_type: form.incident_type || null,
        occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : null,
        witness_names: (form.witness_names || []).filter(Boolean),
        property_damage_value: form.property_damage_value === "" ? null : parseFloat(form.property_damage_value),
      };
      if (editing) await api.put(`/incidents/${editing.id}`, payload);
      else await api.post("/incidents", payload);
      toast.success(editing ? "Updated" : "Incident logged");
      setOpen(false); setEditing(null); setForm(empty);
      // Iter 218 — the AI summary lands async (~3-5s). Immediate reload
      // shows the record, delayed second reload picks up the summary.
      load();
      setTimeout(() => load(), 3500);
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const onClearAiSummary = async (id) => {
    try {
      await api.delete(`/incidents/${id}/ai-summary`);
      toast.success("AI summary removed");
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, ai_summary: null } : x));
    } catch (e) {
      toast.error(errorMessage(e, "Failed to remove AI summary"));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete incident?")) return;
    await api.delete(`/incidents/${id}`); toast.success("Removed"); load();
  };

  const onEdit = (i) => {
    setEditing(i);
    setForm({
      ...empty,
      ...i,
      guard_id: i.guard_id || "",
      incident_type: i.incident_type || "",
      occurred_at: toLocalInput(i.occurred_at),
      witness_names: i.witness_names || [],
      photo_attachments: i.photo_attachments || [],
      property_damage_value: i.property_damage_value ?? "",
    });
    setOpen(true);
  };

  const onSummarize = async (i) => {
    setSummarizing(i.id);
    try {
      const res = await api.post(`/incidents/${i.id}/summarize`);
      toast.success("AI summary ready");
      setItems((prev) => prev.map((x) => x.id === i.id ? { ...x, ai_summary: res.data.summary } : x));
    } catch {
      toast.error("AI summary failed");
    } finally { setSummarizing(null); }
  };

  const sevTone = (s) => ({ critical: "red", high: "red", medium: "amber", low: "blue" }[s] || "default");
  const statTone = (s) => ({ open: "red", investigating: "amber", resolved: "green" }[s] || "default");

  // Parallel array of stable per-row ids so React key isn't the index (prevents
  // stale focus/state when a middle row is removed).
  const [witnessKeys, setWitnessKeys] = useState([]);
  const addWitness = () => {
    setWitnessKeys((k) => [...k, `w-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`]);
    set("witness_names", [...(form.witness_names || []), ""]);
  };
  const setWitness = (i, v) => set("witness_names", form.witness_names.map((x, idx) => (idx === i ? v : x)));
  const rmWitness = (i) => {
    setWitnessKeys((k) => k.filter((_, idx) => idx !== i));
    set("witness_names", form.witness_names.filter((_, idx) => idx !== i));
  };

  const filtered = useMemo(() => {
    const { client_names, site_ids, guard_ids } = filters;
    if (!client_names.length && !site_ids.length && !guard_ids.length) return items;
    const cset = new Set(client_names), sset = new Set(site_ids), gset = new Set(guard_ids);
    return items.filter((i) =>
      (cset.size === 0 || cset.has(i.client_name)) &&
      (sset.size === 0 || sset.has(i.site_id)) &&
      (gset.size === 0 || (i.guard_id && gset.has(i.guard_id)))
    );
  }, [items, filters]);

  // Site options: sorted alphabetically for scannability in the searchable
  // dropdown. Sites with `enabled_reports.incidents=false` are hidden — you
  // can't create an incident on a site that's opted out of the report type.
  const siteOptions = useMemo(() => sites
    .filter((s) => s.enabled_reports?.incidents !== false)
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || "" }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [sites]);

  // Officer options: default to guards currently on-shift at the SELECTED site,
  // but a toggle in the form flips this to the full active roster so ops can
  // attribute an incident to any guard.
  const guardOptions = useMemo(() => {
    if (guardsOnShiftOnly && form.site_id) {
      const ids = new Set(onShiftOfficers.filter((o) => o.site_id === form.site_id).map((o) => o.guard_id));
      return guards
        .filter((g) => ids.has(g.id))
        .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return guards
      .filter((g) => (g.status || "active") !== "inactive" && !g.deactivation_date)
      .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [guardsOnShiftOnly, form.site_id, onShiftOfficers, guards]);

  const hasFilters = !!(filters.client_names.length || filters.site_ids.length || filters.guard_ids.length);

  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";

  return (
    <div className={containerClass}>
      {!isFs && (
        <PageHeader
          title="Incidents"
          subtitle="Log and investigate site incidents. AI-powered summaries by Claude. Photos with timestamps supported."
          action={
            <ActionButton data-testid="add-incident-button" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} style={!canCreateIncident ? { display: "none" } : undefined}>
              <Plus className="size-4" /> Report Incident
            </ActionButton>
          }
        />
      )}

      <div className={isFs ? "p-6 space-y-3" : "p-4 md:p-8 space-y-3"} data-testid="incidents-list">
        <StickyFiltersBar
          testIdPrefix="incidents"
          value={filters}
          onChange={setFilters}
          visibleCount={filtered.length}
          totalCount={items.length}
          isFullscreen={isFs}
          fullscreenUrl={isFs ? null : "/incidents/fullscreen"}
        />

        <BulkResendBar
          kind="incident"
          selected={bulk.selected}
          onClear={bulk.clear}
          testIdPrefix="incidents"
        />

        {filtered.map((i) => (
          <div key={i.id} className="border border-[#27272A] bg-[#121212] p-3 md:p-5 hover:border-[#3F3F46] transition-colors">
            <div className="flex items-start gap-2 md:gap-4">
              <label
                className="mt-1 shrink-0 inline-flex items-center cursor-pointer"
                title="Select for bulk actions"
              >
                <input
                  type="checkbox"
                  data-testid={`incident-select-${i.id}`}
                  checked={bulk.isSelected(i.id)}
                  onChange={() => bulk.toggle(i.id)}
                  className="size-4 accent-[#0A84FF]"
                />
              </label>
              <div className="size-10 shrink-0 border border-[#27272A] hidden sm:flex items-center justify-center">
                <AlertOctagon className="size-5 text-[#FF3B30]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg md:text-xl uppercase font-bold tracking-tight break-words">{i.title}</h3>
                  <PillBadge tone={sevTone(i.severity)}>{i.severity}</PillBadge>
                  {i.status
                    ? <PillBadge tone={statTone(i.status)}>{i.status}</PillBadge>
                    : <span className="text-zinc-600 text-xs italic" data-testid={`incident-status-unset-${i.id}`}>not set</span>}
                  {i.incident_type && <PillBadge>{i.incident_type.replace("_", " ")}</PillBadge>}
                  {i.injuries && <PillBadge tone="red">injuries</PillBadge>}
                  {i.police_called && <PillBadge tone="amber">police</PillBadge>}
                  {i.approval_status && <ApprovalPill status={i.approval_status} testId={`incident-approval-pill-${i.id}`} />}
                </div>
                <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line">{i.description}</div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
                  <span>SITE: {i.site_name}</span>
                  {i.guard_name && <span>OFFICER: {i.guard_name}</span>}
                  {i.location_on_site && <span>LOC: {i.location_on_site}</span>}
                  {i.occurred_at && <span>OCCURRED: {fmtDateTime(i.occurred_at, i.site_timezone)}</span>}
                  <span>LOGGED: {fmtDateTime(i.created_at, i.site_timezone)}</span>
                  {(i.reported_by_name || i.reported_by) && (
                    <span data-testid={`incident-reporter-${i.id}`}>
                      FILED BY: {i.reported_by_name || i.reported_by}
                      {i.reported_by_role ? ` (${(ROLE_LABELS[i.reported_by_role] || i.reported_by_role)})` : ""}
                    </span>
                  )}
                </div>
                {(i.photo_attachments || []).length > 0 && (
                  <button
                    type="button"
                    data-testid={`view-photos-${i.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0A84FF] hover:text-white"
                    onClick={() => setViewingPhotos({ items: i.photo_attachments, title: i.title, tz: i.site_timezone })}
                  >
                    <ImageIcon className="size-3.5" /> {i.photo_attachments.length} attachment{i.photo_attachments.length === 1 ? "" : "s"}
                  </button>
                )}
                {i.ai_summary && (
                  <div className="mt-4 border-l-2 border-[#FF3B30] bg-[#0A0A0A] p-3" data-testid={`incident-ai-summary-${i.id}`}>
                    <div className="label-mute flex items-center gap-2 mb-2">
                      <Sparkles className="size-3" /> AI Analysis (Claude)
                      <button
                        type="button"
                        onClick={() => onClearAiSummary(i.id)}
                        className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
                        data-testid={`incident-ai-summary-remove-${i.id}`}
                        title="Remove AI summary"
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <div className="text-sm whitespace-pre-line text-zinc-300">{i.ai_summary}</div>
                  </div>
                )}
                <AdminCommentsPanel kind="incidents" targetId={i.id} />
                {i.approval_status && (
                  <div className="mt-4">
                    <ApprovalActions kind="incident" report={i} onChange={() => load()} />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <GhostButton
                  data-testid={`summarize-incident-${i.id}`}
                  onClick={() => onSummarize(i)}
                  disabled={summarizing === i.id}
                >
                  {summarizing === i.id ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
                  AI
                </GhostButton>
                <GhostButton onClick={() => onEdit(i)}>Update</GhostButton>
                <ResendReportEmailButton kind="incident" itemId={i.id} testId={`incident-resend-email-${i.id}`} />
                <GhostButton onClick={() => onDelete(i.id)} style={!canDeleteIncident ? { display: "none" } : undefined}><Trash2 className="size-3.5" /></GhostButton>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">No incidents reported. All clear.</div>}
        {items.length > 0 && filtered.length === 0 && (
          <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">No incidents match the filters.</div>
        )}
      </div>

      {open && (
        <Modal title={editing ? "Update Incident" : "Report Incident"} onClose={() => setOpen(false)} testId="incident-form-modal">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><FormField label="Title *"><input data-testid="incident-title-input" className={inputClass} value={form.title} onChange={(e) => set("title", e.target.value)} /></FormField></div>
            <FormField label="Site *">
              <SingleSelectDropdown
                testId="incident-site"
                label="Select site…"
                options={siteOptions}
                value={form.site_id}
                onChange={(v) => set("site_id", v)}
                searchable
                triggerClassName="w-full"
              />
            </FormField>
            <FormField label="Officer (optional)">
              <div className="space-y-1">
                <SingleSelectDropdown
                  testId="incident-guard"
                  label="Select …"
                  options={guardOptions}
                  value={form.guard_id}
                  onChange={(v) => set("guard_id", v)}
                  searchable
                  triggerClassName="w-full"
                />
                <label
                  data-testid="incident-guard-onshift-toggle"
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-300"
                >
                  <input
                    type="checkbox"
                    className="accent-[#FF3B30]"
                    checked={guardsOnShiftOnly}
                    onChange={(e) => setGuardsOnShiftOnly(e.target.checked)}
                    disabled={!form.site_id}
                  />
                  On-shift at this site only
                </label>
              </div>
            </FormField>
            <FormField label="Type">
              <select className={inputClass} value={form.incident_type} onChange={(e) => set("incident_type", e.target.value)}>
                {INCIDENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </FormField>
            <FormField label="Severity">
              <select className={inputClass} value={form.severity} onChange={(e) => set("severity", e.target.value)}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </FormField>
            {canEditStatus && (
              <FormField label="Status">
                <select
                  className={inputClass}
                  data-testid="incident-status-select"
                  value={form.status ?? ""}
                  onChange={(e) => set("status", e.target.value || null)}
                >
                  <option value="">— none —</option>
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="resolved">Resolved</option>
                </select>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 block">
                  Leave &ldquo;none&rdquo; until you&rsquo;re ready to triage.
                </span>
              </FormField>
            )}
            <FormField label="Occurred at">
              <input type="datetime-local" className={inputClass} value={form.occurred_at} onChange={(e) => set("occurred_at", e.target.value)} />
            </FormField>
            <div className="col-span-2"><FormField label="Location on site">
              <input className={inputClass} placeholder="e.g. loading dock NE" value={form.location_on_site} onChange={(e) => set("location_on_site", e.target.value)} />
            </FormField></div>
            <div className="col-span-2"><FormField label="Narrative *">
              <textarea rows={4} className={inputClass} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What happened? Be specific." />
            </FormField></div>

            {/* People involved */}
            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-3">People Involved</div>
            </div>
            <FormField label="Victim name"><input className={inputClass} value={form.victim_name} onChange={(e) => set("victim_name", e.target.value)} /></FormField>
            <FormField label="Suspect description"><input className={inputClass} value={form.suspect_description} onChange={(e) => set("suspect_description", e.target.value)} /></FormField>
            <div className="col-span-2">
              <FormField label="Witnesses">
                <div className="space-y-1">
                  {(form.witness_names || []).map((w, idx) => (
                    <div key={witnessKeys[idx] || `w-idx-${idx}`} className="flex gap-1">
                      <input className={inputClass + " flex-1"} value={w} onChange={(e) => setWitness(idx, e.target.value)} placeholder="Name" />
                      <GhostButton type="button" onClick={() => rmWitness(idx)}><Trash2 className="size-3.5" /></GhostButton>
                    </div>
                  ))}
                  <GhostButton type="button" onClick={addWitness}><Plus className="size-3.5" /> Add witness</GhostButton>
                </div>
              </FormField>
            </div>

            {/* Police + Injuries + Damage */}
            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-3">Authorities & Impact</div>
            </div>
            <FormField label="">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="police-called" checked={form.police_called} onChange={(e) => set("police_called", e.target.checked)} />
                Police were called
              </label>
            </FormField>
            <FormField label="Police report #">
              <input className={inputClass} value={form.police_report_no} onChange={(e) => set("police_report_no", e.target.value)} disabled={!form.police_called} />
            </FormField>
            <FormField label="">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="injuries-checkbox" checked={form.injuries} onChange={(e) => set("injuries", e.target.checked)} />
                Injuries occurred
              </label>
            </FormField>
            <FormField label="Injuries description">
              <input className={inputClass} value={form.injuries_description} onChange={(e) => set("injuries_description", e.target.value)} disabled={!form.injuries} />
            </FormField>
            <FormField label="">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="property-damage-checkbox" checked={form.property_damage} onChange={(e) => set("property_damage", e.target.checked)} />
                Property damage
              </label>
            </FormField>
            <FormField label="Damage est. value ($)">
              <input type="number" min="0" step="0.01" className={inputClass} value={form.property_damage_value} onChange={(e) => set("property_damage_value", e.target.value)} disabled={!form.property_damage} />
            </FormField>
            <div className="col-span-2"><FormField label="Property damage description">
              <input className={inputClass} value={form.property_damage_description} onChange={(e) => set("property_damage_description", e.target.value)} disabled={!form.property_damage} />
            </FormField></div>

            {/* Action + Follow-up */}
            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-3">Action & Follow-up</div>
            </div>
            <div className="col-span-2"><FormField label="Action taken">
              <textarea rows={2} className={inputClass} value={form.action_taken} onChange={(e) => set("action_taken", e.target.value)} placeholder="What did the  or supervisor do?" />
            </FormField></div>
            <FormField label="">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.follow_up_needed} onChange={(e) => set("follow_up_needed", e.target.checked)} />
                Follow-up needed
              </label>
            </FormField>
            <FormField label="Follow-up notes">
              <input className={inputClass} value={form.follow_up_notes} onChange={(e) => set("follow_up_notes", e.target.value)} disabled={!form.follow_up_needed} />
            </FormField>

            {/* Photos */}
            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-3">Photos</div>
              <PhotoAttachmentsField
                value={form.photo_attachments}
                onChange={(v) => set("photo_attachments", v)}
                purpose="incident"
                site={sites.find((s) => s.id === form.site_id) || null}
                guardName={user?.name || user?.email}
                allowVideo
                testIdPrefix="incident-photo"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
            <ActionButton data-testid="save-incident-button" onClick={onSave}>Save</ActionButton>
          </div>
        </Modal>
      )}

      {viewingPhotos && (
        <Modal title={`Attachments · ${viewingPhotos.title}`} onClose={() => setViewingPhotos(null)} testId="photo-viewer-modal">
          <AttachmentViewer
            items={viewingPhotos.items}
            formatTimestamp={(iso) => fmtDateTime(iso, viewingPhotos.tz)}
            testIdPrefix="incident-attachment"
          />
        </Modal>
      )}
    </div>
  );
}
